import React, { useState } from 'react';
import { Notification } from '@shootsta/common-react';
import { HalloweenPromoModal, parseTopupPromo } from '@core/shared';

type Props = {
  promo: unknown;
  organisationId?: string;
};

type BannerColour = 'orange' | 'yellow' | 'green' | 'red';

const BANNER_COLOURS: BannerColour[] = ['orange', 'yellow', 'green', 'red'];

type Banner = {
  text: string;
  colour: BannerColour;
  ctaText?: string;
  ctaUrl?: string;
};

const nonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * The banner's own fields from the `topup-credits-promos` flag. A promo can
 * be banner-only (e.g. the end-of-year Mini Kit draw): it leaves out
 * `buyQuantity`/`freeQuantity`, so no free credits are granted anywhere, and
 * the banner still shows. `ctaUrl` must be https.
 */
const parseBanner = (value: unknown): Banner | null => {
  if (!value || typeof value !== 'object') return null;
  const { enabled, bannerText, bannerColour, ctaText, ctaUrl } =
    value as Record<string, unknown>;

  if (enabled !== true || !nonEmptyString(bannerText)) return null;

  return {
    text: bannerText,
    colour: BANNER_COLOURS.includes(bannerColour as BannerColour)
      ? (bannerColour as BannerColour)
      : 'orange',
    ctaText: nonEmptyString(ctaText) ? ctaText : undefined,
    ctaUrl:
      nonEmptyString(ctaUrl) && ctaUrl.startsWith('https://')
        ? ctaUrl
        : undefined
  };
};

const dismissKey = (organisationId: string) =>
  `topupPromoDismissed:${organisationId}`;

// A dismissal is remembered per workspace against the banner text, so a new
// promo (new text in LaunchDarkly) shows again for everyone.
const readDismissedText = (organisationId?: string) => {
  if (!organisationId) return null;
  try {
    return localStorage.getItem(dismissKey(organisationId));
  } catch {
    return null;
  }
};

/**
 * SOW 49: the top-up promo banner, driven by the `topup-credits-promos` flag.
 * With a `ctaUrl` its button opens that page in a new tab; otherwise, for a
 * free-credits promo, "Buy credits" opens the offer popup that goes straight
 * to Stripe checkout.
 */
export default function PromoBanner({
  promo: rawPromo,
  organisationId
}: Props) {
  const banner = parseBanner(rawPromo);
  const creditsPromo = parseTopupPromo(rawPromo);
  const [offerOpen, setOfferOpen] = useState(false);
  // Hides the banner straight away on dismiss, and still does when storage
  // is blocked. The stored dismissal is read each render (one localStorage
  // read), so switching workspace picks up that workspace's own dismissal.
  const [hiddenThisSession, setHiddenThisSession] = useState<string | null>(
    null
  );
  const dismissedText = readDismissedText(organisationId);

  if (
    !banner ||
    dismissedText === banner.text ||
    hiddenThisSession === banner.text
  ) {
    return null;
  }

  const onDismiss = () => {
    if (organisationId) {
      try {
        localStorage.setItem(dismissKey(organisationId), banner.text);
      } catch {
        // Storage blocked: the banner comes back on the next reload.
      }
    }
    setHiddenThisSession(banner.text);
  };

  let callToActionText = '';
  let onClickCallToAction: (() => void) | null = null;
  if (banner.ctaUrl) {
    const { ctaUrl } = banner;
    callToActionText = banner.ctaText || 'Learn more';
    onClickCallToAction = () => {
      window.open(ctaUrl, '_blank', 'noopener,noreferrer');
    };
  } else if (creditsPromo) {
    callToActionText = banner.ctaText || 'Buy credits';
    onClickCallToAction = () => setOfferOpen(true);
  }

  return (
    <div style={{ width: '100%' }}>
      <Notification
        fullWidthStyle
        backgroundColour={banner.colour}
        text={banner.text}
        callToActionText={callToActionText}
        onClickCallToAction={onClickCallToAction}
        onClickCloseBtn={onDismiss}
      />
      {offerOpen && creditsPromo && organisationId && (
        <HalloweenPromoModal
          visible
          promo={creditsPromo}
          organisationId={organisationId}
          onClose={() => setOfferOpen(false)}
        />
      )}
    </div>
  );
}
